import React from 'react';
import { useCart } from '../context/CartContext';
import { showNotification } from '../utils/notification';
import { useBasicInfo } from '../data/menuData';

const CartModal = ({ isOpen, onClose }) => {
  const { basicInfo, loading } = useBasicInfo();
  const { items, updateQuantity, removeItem, clearCart, getTotalPrice } = useCart();

  const handlePlaceOrder = () => {
    if (items.length === 0) {
      showNotification('Your cart is empty!', 'error');
      return;
    }

    let message = `🍽️ *New Order from Vanilla Sky Bakehouse* 🍽️\n\n`;
    message += `*Order Details:*\n`;

    items.forEach((item, index) => {
      message += `${index + 1}. ${item.name}\n`;
      if (item.details) message += `   ${item.details}\n`;
      message += `   Quantity: ${item.quantity}\n`;
      message += `   Price: ₹${(item.price * item.quantity).toFixed(2)}\n\n`;
    });

    const total = getTotalPrice();
    message += `*Total Amount: ₹${total.toFixed(2)}*\n\n`;
    message += `Please confirm this order and provide delivery details. Thank you! `;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${basicInfo?.contactNo || '919051158336'}&text=${encodedMessage}`;

    window.open(whatsappUrl, '_blank');
    clearCart();
    onClose();
    showNotification('Order sent to WhatsApp!', 'success');
  };

  const handleClearCart = () => {
    clearCart();
    showNotification('Cart cleared', 'info');
  };

  if (!isOpen) return null;

  return (
    <div className="cart-modal fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-[#FFFDF8]/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl lg:rounded-3xl w-full max-w-sm sm:max-w-md lg:max-w-2xl max-h-[90vh] sm:max-h-[85vh] overflow-hidden shadow-2xl border border-[#f1dac1]/40">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#F8E8D2] to-[#f5dec0]/90 p-4 sm:p-6 flex justify-between items-center border-b border-[#e9d2b5]/60">
          <h3 className="font-dm-sans font-bold text-lg sm:text-xl text-gray-800 flex items-center gap-3">
            <div className="w-8 h-8 bg-white/80 rounded-full flex items-center justify-center shadow-sm">
              <i className="fas fa-shopping-bag text-[#D4AF37] text-sm"></i>
            </div>
            Your Order
          </h3>
          <button 
            onClick={onClose}
            className="close-cart w-8 h-8 bg-white/80 hover:bg-white text-gray-600 hover:text-gray-800 rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-300 hover:scale-110"
          >
            <i className="fas fa-times text-sm"></i>
          </button>
        </div>

        {/* Cart Items */}
        <div className="p-4 sm:p-6 max-h-64 sm:max-h-96 overflow-y-auto bg-gradient-to-b from-white to-[#F8E8D2]/10">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-[#F8E8D2]/30 rounded-full flex items-center justify-center mb-4">
                <i className="fas fa-shopping-bag text-[#F8E8D2] text-xl"></i>
              </div>
              <p className="text-gray-500 text-sm sm:text-base">Your cart is empty</p>
              <p className="text-gray-400 text-xs sm:text-sm mt-1">Add some delicious items to get started!</p>
            </div>
          ) : (
            items.map((item, index) => (
              <div key={index} className="flex justify-between items-center p-4 sm:p-5 mb-3 bg-white/60 backdrop-blur-sm rounded-xl border border-[#F8E8D2]/30 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">{item.name}</div>
                  <div className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                    <i className="fas fa-weight-hanging text-[#F8E8D2]"></i>
                    {item.details || 'Standard size'}
                  </div>
                </div>
                <div className="font-bold text-gray-800 mx-3 sm:mx-4 text-sm sm:text-base">
                  ₹{(item.price * item.quantity).toFixed(2)}
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => updateQuantity(index, -1)}
                    className="quantity-btn w-7 h-7 bg-[#F8E8D2] hover:bg-[#F8E8D2]/80 text-gray-700 rounded-full flex items-center justify-center text-sm hover:scale-110 transition-all duration-300 shadow-sm"
                  >
                    <i className="fas fa-minus text-xs"></i>
                  </button>
                  <span className="font-semibold text-gray-800 min-w-[24px] text-center text-sm sm:text-base">
                    {item.quantity}
                  </span>
                  <button 
                    onClick={() => updateQuantity(index, 1)}
                    className="quantity-btn w-7 h-7 bg-[#F8E8D2] hover:bg-[#F8E8D2]/80 text-gray-700 rounded-full flex items-center justify-center text-sm hover:scale-110 transition-all duration-300 shadow-sm"
                  >
                    <i className="fas fa-plus text-xs"></i>
                  </button>
                </div>
                <button 
                  onClick={() => removeItem(index)}
                  className="remove-item w-7 h-7 bg-red-100 hover:bg-red-200 text-red-600 rounded-full flex items-center justify-center ml-2 hover:scale-110 transition-all duration-300 shadow-sm"
                >
                  <i className="fas fa-trash text-xs"></i>
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="bg-gradient-to-br from-white to-[#f8ecd8]/40 p-4 sm:p-6 border-t border-[#ead2b0]/40">
          <div className="flex justify-between items-center text-lg sm:text-xl font-bold text-gray-800 mb-4 sm:mb-6">
            <span className="flex items-center gap-2">
              <i className="fas fa-receipt text-[#D4AF37]"></i>
              Total:
            </span>
            <span className="text-2xl sm:text-3xl font-bold text-gray-900">
              ₹{getTotalPrice().toFixed(2)}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button 
              onClick={handleClearCart}
              className="flex-1 px-4 sm:px-6 py-3 sm:py-4 border-2 border-[#D4AF37] text-gray-700 font-semibold rounded-xl hover:bg-[#f5deb3] hover:text-gray-800 transition-all duration-300 text-sm sm:text-base flex items-center justify-center gap-2 hover:scale-105 shadow-sm hover:shadow-md"
            >
              <i className="fas fa-trash-alt"></i>
              Clear Cart
            </button>
            <button 
              onClick={handlePlaceOrder}
              className="flex-1 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-[#F8E8D2] to-[#e8d1ae]/90 text-gray-800 font-semibold rounded-xl hover:from-[#f2dfc2] hover:to-[#e8d1ae] hover:-translate-y-1 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-3 text-sm sm:text-base shadow-md"
            >
              <i className="fab fa-whatsapp text-green-600"></i>
              Place Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartModal; 